import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ViewDidEnter, ViewWillLeave } from '@ionic/angular';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { Note } from '@usf/alert-types';
import { Customer } from '@usf/customer-types';
import { NoteStoreService, NOTES_ACTIONS } from '@usf/ngrx-alerts';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, first, take, timeout } from 'rxjs/operators';
import { PdpLnkSrc } from 'src/app/shared/constants/pdp-lnksrc.enum';
import { CustomerStoreService } from '../../../ngrx-customer/services';
import { Product } from '../../../shared/models/product.model';
import { ProductService } from '../../../shared/services/product/product.service';
import { NotificationsService } from '../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'app-tm-note-details',
  templateUrl: './tm-note-details.page.html',
  styleUrls: ['./tm-note-details.page.scss'],
})
export class TmNoteDetailsPage implements ViewDidEnter, ViewWillLeave {
  noteId: string;
  note: Note;
  customer: Customer;
  isTouchExperience: boolean;
  isDesktop: boolean;
  products: Product[] = [];
  lnkSrc = PdpLnkSrc.tmNotes;

  isPageLoadTracked = false;
  customer$: Subscription;
  customerWithNumber$: Subscription;
  getNoteActionSub$: Subscription;
  note$: Subscription;
  onEnterActionSub$: Subscription;
  productList$: Subscription;
  customerNumberFromPush: string;
  errorShowed: boolean;
  currentCustomerNumber: string;

  products$: Observable<Product[]>;

  constructor(
    private noteStoreService: NoteStoreService,
    private notificationsService: NotificationsService,
    private route: ActivatedRoute,
    private productService: ProductService,
    private customerService: CustomerStoreService,
    private analyticsService: ProductAnalyticsService,
    private actionSubject: ActionsSubject,
    private panAppState: PanAppState,
    private translateService: TranslateService,
    platformService: PlatformService,
  ) {
    firstValueFrom(platformService.isTouchExperience$).then(
      (isTouchExperience: boolean) => {
        this.isTouchExperience = isTouchExperience;
      },
    );
    this.isDesktop = platformService.platformType === PlatformEnum.desktop;
  }

  ionViewDidEnter(): void {
    this.noteId = this.route?.snapshot?.params['noteId'];
    if (this.route?.snapshot?.queryParamMap) {
      this.customerNumberFromPush =
        this.route?.snapshot?.queryParamMap.get('customerNumber');
    }
    if (this.customerNumberFromPush) {
      this.customer$ = this.customerService
        .loadSelectedCustomer$()
        ?.pipe(
          filter(customer => !!customer),
          take(1),
        )
        ?.subscribe(customer => {
          this.customer = customer;
          this.currentCustomerNumber = this.customer.customerNumber.toString();
          this.onEnterActionSub$ = this.actionSubject
            .pipe(ofType(NOTES_ACTIONS.getNotesSuccess), take(1))
            .subscribe(data => {
              this.getNote();
            });
        });
    } else {
      this.customer$ = this.customerService
        .loadSelectedCustomer$()
        ?.pipe(
          filter(customer => !!customer),
          take(1),
        )
        ?.subscribe(customer => {
          this.customer = customer;
          this.getNote();
        });
    }
    this.setPageTitle();
  }

  setPageTitle() {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.noteDetails),
    );
  }

  ionViewWillLeave(): void {
    if (this.customer$) {
      this.customer$?.unsubscribe();
    }
    if (this.customerWithNumber$) {
      this.customerWithNumber$?.unsubscribe();
    }
    if (this.getNoteActionSub$) {
      this.getNoteActionSub$?.unsubscribe();
    }
    if (this.note$) {
      this.note$?.unsubscribe();
    }
    if (this.onEnterActionSub$) {
      this.onEnterActionSub$?.unsubscribe();
    }
    if (this.productList$) {
      this.productList$?.unsubscribe();
    }
  }

  getNote() {
    this.note$ = this.noteStoreService
      .getNoteWithId(this.noteId)
      .pipe(take(1))
      .subscribe(note => {
        if (note) {
          this.note = note;
          let expDate =
            typeof note.expireDate === 'string'
              ? new Date(note.expireDate)
              : note.expireDate;
          let today = new Date();
          today.setUTCHours(0, 0, 0, 0);
          if (expDate < today) {
            // If the note is expired; route to TM Note tab with error modal
            if (!this.errorShowed) {
              this.notificationsService.handleNoteError();
              this.errorShowed = true;
            }
          } else if (note?.noteProdNbrs?.length > 0) {
            this.loadProducts(note.noteProdNbrs);
          } else {
            this.analyticsService.trackTMNoteDetailPageLoad(
              this.note?.noteId,
              [],
            );
          }
          this.notificationsService.markAsReadTmNote(note?.noteId.toString());
        } else {
          this.getNoteActionSub$ = this.actionSubject
            .pipe(
              ofType(NOTES_ACTIONS.getNotesSuccess),
              take(1),
              timeout(6000),
              catchError(() => {
                // handle note not found
                this.notificationsService.handleNoteError();
                this.errorShowed = true;
                return null;
              }),
            )
            .subscribe(() => {
              this.getNote();
            });
        }
      });
  }

  loadProducts(productNumbers) {
    this.productService.loadProducts(productNumbers);
    this.productList$ = this.productService
      .getProductsWithAlternatives(productNumbers)
      .pipe(filter(p => [...p].every(([n, p]) => p !== undefined)))
      .subscribe(products => {
        const productsFormat = Array.from(products.values());
        if (!this.isPageLoadTracked) {
          const trackingData = productsFormat.reduce((acum, p) => {
            const data: any = [
              {
                divisionNumber: p?.summary?.divisionNumber,
                productNumber: p?.summary?.productNumber,
                attributes: p?.trackingAttributes,
              },
            ];
            if (p?.hasAlternative) {
              data[0] = {
                ...data[0],
                exceptionView: '1',
                exceptionNumberofSubs:
                  p?.inventory?.substituteProducts?.length || '1',
              };
              data.push({
                divisionNumber:
                  p?.alternative?.product?.summary?.divisionNumber,
                productNumber: p?.alternative?.product?.productNumber,
                attributes: p?.alternative?.product.trackingAttributes,
                substitutionView: '1',
                substitutionParentApn: `${p?.summary?.divisionNumber}-${p?.productNumber}`,
              });
            }
            return [...acum, ...data];
          }, []);
          this.analyticsService.trackTMNoteDetailPageLoad(
            this.note?.noteId,
            trackingData,
          );
          this.isPageLoadTracked = true;
        }
        this.products = productsFormat;
      });
  }

  trackItem(index: any, item: any) {
    return item ? index : undefined;
  }
}
